import { useWeb3React } from '@web3-react/core';
import { useCallback } from 'react';
import {
    Contract,
    parseUnits,
    formatUnits,
    getAddress,
    ZeroAddress,
} from 'ethers';
import config from "../../config/config";

// ERC20 ABI 수정 - 핵심 함수만 포함
const ERC20_ABI = [{"inputs":[{"internalType":"string","name":"_name","type":"string"},{"internalType":"string","name":"_symbol","type":"string"}],"stateMutability":"nonpayable","type":"constructor"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"owner","type":"address"},{"indexed":true,"internalType":"address","name":"spender","type":"address"},{"indexed":false,"internalType":"uint256","name":"value","type":"uint256"}],"name":"Approval","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"previousOwner","type":"address"},{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}],"name":"OwnershipTransferred","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Paused","type":"event"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"from","type":"address"},{"indexed":true,"internalType":"address","name":"to","type":"address"},{"indexed":false,"internalType":"uint256","name":"value","type":"uint256"}],"name":"Transfer","type":"event"},{"anonymous":false,"inputs":[{"indexed":false,"internalType":"address","name":"account","type":"address"}],"name":"Unpaused","type":"event"},{"inputs":[{"internalType":"address","name":"owner","type":"address"},{"internalType":"address","name":"spender","type":"address"}],"name":"allowance","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"spender","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"approve","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"account","type":"address"}],"name":"balanceOf","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"burn","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"account","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"burnFrom","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"decimals","outputs":[{"internalType":"uint8","name":"","type":"uint8"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"spender","type":"address"},{"internalType":"uint256","name":"subtractedValue","type":"uint256"}],"name":"decreaseAllowance","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"spender","type":"address"},{"internalType":"uint256","name":"addedValue","type":"uint256"}],"name":"increaseAllowance","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"to","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"mint","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"minter","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"name","outputs":[{"internalType":"string","name":"","type":"string"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"owner","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"pause","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"paused","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"newMinter","type":"address"}],"name":"setMinter","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"symbol","outputs":[{"internalType":"string","name":"","type":"string"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"totalSupply","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[{"internalType":"address","name":"to","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"transfer","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"from","type":"address"},{"internalType":"address","name":"to","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"transferFrom","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"newOwner","type":"address"}],"name":"transferOwnership","outputs":[],"stateMutability":"nonpayable","type":"function"},{"inputs":[],"name":"unpause","outputs":[],"stateMutability":"nonpayable","type":"function"}];

const useEtherTokenTransfer = () => {
    const { provider, account, chainId } = useWeb3React();

    const waitForTransaction = async (tx) => {
        try {
            // tx.hash를 사용하여 provider를 통해 트랜잭션 결과 모니터링
            const receipt = await provider.waitForTransaction(tx.hash, 1); // confirmations = 1

            // 트랜잭션 실패 시
            if (!receipt) {
                throw new Error('Transaction failed: receipt not found');
            }

            // 추가로 트랜잭션 상세 정보 가져오기
            const transaction = await provider.getTransaction(tx.hash);

            return {
                status: receipt.status === 1,
                hash: receipt.hash,
                blockNumber: receipt.blockNumber,
                from: transaction.from,
                to: transaction.to,
                gasUsed: receipt.gasUsed,
                effectiveGasPrice: receipt.effectiveGasPrice,
                confirmations: receipt.confirmations,
                transactionIndex: receipt.transactionIndex
            };
        } catch (error) {
            console.error('Transaction wait error:', error);
            throw new Error(`트랜잭션 확인 실패: ${error.message}`);
        }
    };

    const transferToken = useCallback(
        async (tokenType, recipientAddress, value) => {
            if (!provider || !account) {
                throw new Error('지갑이 연결되어 있지 않습니다');
            }
            console.log('전송 정보:', { tokenType, recipientAddress, value, chainId });

            let transactionResult = false;
            let txHash = null;
            let result = null;

            try {
                const signer = await provider.getSigner();
                const feeData = await provider.getFeeData();

                console.log('수수료 정보:', feeData);


                if (tokenType === 'ETH' || tokenType === 'HVH') {
                    const balance = await provider.getBalance(account);
                    const tokenValue = parseUnits(value.toString(), 18);

                    if (balance < tokenValue) {
                        throw new Error('잔액이 부족합니다');
                    }

                    // 단순화된 트랜잭션 전송
                    const tx = await signer.sendTransaction({
                        to: recipientAddress,
                        value: tokenValue
                    });

                    console.log('ETH 트랜잭션 전송됨:', tx.hash);
                    result = await waitForTransaction(tx);
                    txHash = tx.hash;
                    transactionResult = result.status;

                } else {
                    const tokenAddress = getTokenAddress(tokenType);
                    console.log('토큰 컨트랙트 주소:', tokenAddress);

                    if (tokenAddress === ZeroAddress) {
                        throw new Error('유효하지 않은 토큰 주소입니다');
                    }

                    try {
                        const tokenContract = new Contract(
                            tokenAddress,
                            ERC20_ABI,
                            signer
                        );

                        const decimals = await tokenContract.decimals();
                        const balance = await tokenContract.balanceOf(account);

                        console.log('토큰 정보:', {
                            decimals: Number(decimals),
                            balance: formatUnits(balance, decimals)
                        });

                        const tokenValue = parseUnits(value.toString(), decimals);

                        if (balance < tokenValue) {
                            throw new Error('토큰 잔액이 부족합니다');
                        }

                        // estimateGas
                        const gasLimit = await tokenContract.transfer.estimateGas(
                            recipientAddress,
                            tokenValue
                        );

                        const tx = await tokenContract.transfer(
                            recipientAddress,
                            tokenValue,
                            {
                                gasLimit: (gasLimit.mul(150).div(100)).toNumber(),
                                gasPrice: (feeData.gasPrice.mul(150).div(100)).toNumber(),
                            }
                        );

                        txHash = tx.hash;
                        result = await waitForTransaction(tx);
                        transactionResult = result.status;

                    } catch (error) {
                        console.error('토큰 전송 중 오류:', error);
                        if (error.message.includes('insufficient funds')) {
                            throw new Error('가스비가 부족합니다');
                        }
                        throw new Error(`토큰 전송 실패: ${error.message}`);
                    }
                }

                console.log('트랜잭션 성공:', {
                    txHash,
                    result
                });

            } catch (error) {
                console.error('트랜잭션 실패:', error);
                throw error;
            }

            return { txHash, transactionResult, result };
        },
        [provider, account, chainId]
    );

    // 토큰 주소 조회 함수
    const getTokenAddress = (tokenType) => {

        const TOKEN_ADDRESSES = config.TOKEN_CONTRACT_ADDRESS;

        const address = TOKEN_ADDRESSES[tokenType.toLowerCase()];
        if (!address) {
            throw new Error(`알 수 없는 토큰 타입: ${tokenType}`);
        }

        return getAddress(address);
    };

    return transferToken;
};

export default useEtherTokenTransfer;